import { Link, useLocation } from 'react-router-dom'
import { 
  Home, 
  Ship, 
  Globe, 
  FlaskConical, 
  Users, 
  Trophy,
  Shield,
  Crown,
  Zap,
  Settings,
  User,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  MessageSquare,
  Building,
  Wrench,
  Target,
  Search,
  MessageCircle,
  Star,
  Factory,
  Eye,
  BookOpen,
  Award,
  BarChart3,
  MapPin,
  Sword,
  Gamepad2,
  FileText,
  Zap as Bot,
  File,
  FileText as Newspaper,
  Calendar,
  BookOpen as GraduationCap,
  HelpCircle,
  Package,
  Bell,
  Gift,
  Crown as Gavel,
  Settings as ToggleLeft,
  User as UserCog,
  Activity,
  ShoppingBag,
  CreditCard,
  FileText as Receipt
} from 'lucide-react'

interface User {
  id: string
  name: string
  email: string
  level: number
  faction?: string
}

interface SidebarProps {
  user: User | null
  collapsed: boolean
  onToggleCollapse: () => void
}

const Sidebar = ({ user, collapsed, onToggleCollapse }: SidebarProps) => {
  const location = useLocation()

  const navigationItems = [
    // Core
    { path: '/dashboard', label: 'Dashboard', icon: Home, category: 'core' },
    { path: '/health', label: 'Health Monitor', icon: Activity, category: 'core' },
    { path: '/economy', label: 'Economy', icon: TrendingUp, category: 'core' },
    { path: '/fleet-management', label: 'Fleet', icon: Ship, category: 'core' },
    { path: '/diplomacy', label: 'Diplomacy', icon: Crown, category: 'core' },
    { path: '/galaxy-map', label: 'Galaxy Map', icon: Globe, category: 'core' },
    { path: '/research-center', label: 'Research', icon: FlaskConical, category: 'core' },
    { path: '/combat', label: 'Combat', icon: Sword, category: 'core' },
    { path: '/shipyard', label: 'Shipyard', icon: Factory, category: 'core' },
    { path: '/units', label: 'Units', icon: Users, category: 'core' },
    { path: '/unit-details', label: 'Unit Details', icon: User, category: 'core' },
    { path: '/fleet-units', label: 'Fleet Units', icon: Ship, category: 'core' },
    { path: '/battle-report', label: 'Battle Report', icon: FileText, category: 'core' },
    { path: '/ai-battle-report', label: 'AI Battle Report', icon: Bot, category: 'core' },
    { path: '/battle-reports', label: 'Battle Reports', icon: File, category: 'core' },
    
    // Social
    { path: '/market', label: 'Market', icon: Gamepad2, category: 'social' },
    { path: '/alliances', label: 'Alliances', icon: Shield, category: 'social' },
    { path: '/sector-hub', label: 'Sector Hub', icon: MapPin, category: 'social' },
    { path: '/rankings', label: 'Rankings', icon: Trophy, category: 'social' },
    { path: '/chat', label: 'Chat', icon: MessageCircle, category: 'social' },
    
    // Community
    { path: '/news', label: 'News', icon: Newspaper, category: 'community' },
    { path: '/forums', label: 'Forums', icon: MessageSquare, category: 'community' },
    { path: '/events', label: 'Events', icon: Calendar, category: 'community' },
    { path: '/blog', label: 'Blog', icon: BookOpen, category: 'community' },
    { path: '/community-hub', label: 'Community Hub', icon: Users, category: 'community' },
    
    // User
    { path: '/profile', label: 'Profile', icon: User, category: 'user' },
    { path: '/achievements', label: 'Achievements', icon: Award, category: 'user' },
    { path: '/tutorial', label: 'Tutorial', icon: GraduationCap, category: 'user' },
    { path: '/settings', label: 'Settings', icon: Settings, category: 'user' },
    { path: '/support', label: 'Support', icon: HelpCircle, category: 'user' },
    { path: '/inventory', label: 'Inventory', icon: Package, category: 'user' },
    { path: '/missions', label: 'Missions', icon: Target, category: 'user' },
    { path: '/notifications', label: 'Notifications', icon: Bell, category: 'user' },
    { path: '/rewards', label: 'Rewards', icon: Gift, category: 'user' },
    
    // Admin
    { path: '/intelligence', label: 'Intelligence', icon: Eye, category: 'admin' },
    { path: '/council', label: 'Council', icon: Gavel, category: 'admin' },
    { path: '/admin-panel', label: 'Admin Panel', icon: Shield, category: 'admin' },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, category: 'admin' },
    { path: '/feature-toggle', label: 'Feature Toggle', icon: ToggleLeft, category: 'admin' },
    { path: '/user-management', label: 'User Management', icon: UserCog, category: 'admin' },
    { path: '/system-health', label: 'System Health', icon: Activity, category: 'admin' },
    
    // Economy Store
    { path: '/store', label: 'Store', icon: ShoppingBag, category: 'economy-store' },
    { path: '/subscriptions', label: 'Subscriptions', icon: CreditCard, category: 'economy-store' },
    { path: '/purchase-history', label: 'Purchase History', icon: Receipt, category: 'economy-store' },
    
    // Events & Tournaments
    { path: '/tournaments', label: 'Tournaments', icon: Trophy, category: 'events' },
  ]

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case 'main': return 'Command'
      case 'economy': return 'Economy'
      case 'social': return 'Social'
      case 'user': return 'Account'
      default: return ''
    }
  }

  const renderNavItem = (item: typeof navigationItems[0]) => {
    const Icon = item.icon
    const isActive = location.pathname === item.path
    
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'} py-3 mx-2 rounded-lg transition-all duration-200 group ${
          isActive
            ? 'bg-purple-600/80 text-white shadow-lg shadow-purple-500/20 backdrop-blur-sm'
            : 'text-gray-300 hover:text-white hover:bg-white/10 backdrop-blur-sm'
        }`}
        title={collapsed ? item.label : ''}
      >
        <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} ${isActive ? 'text-white' : ''}`} />
        {!collapsed && (
          <span className="text-sm font-medium">{item.label}</span>
        )}
        {!collapsed && isActive && (
          <div className="w-2 h-2 bg-white rounded-full ml-auto"></div>
        )}
      </Link>
    )
  }

  const groupedItems = navigationItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = []
    }
    acc[item.category].push(item)
    return acc
  }, {} as Record<string, typeof navigationItems>)

  return (
    <div className={`fixed left-0 top-0 h-full bg-black/20 backdrop-blur-md border-r border-purple-500/30 transition-all duration-300 z-30 ${
      collapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        {!collapsed && (
          <div className="flex items-center space-x-2">
            <Crown className="w-6 h-6 text-blue-600" />
            <span className="text-lg font-bold text-gray-900">
              Crown of Suns
            </span>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-1 rounded-lg hover:bg-white/10 transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>

      {/* User Info */}
      {user && (
        <div className={`p-4 border-b border-white/10 ${collapsed ? 'text-center' : ''}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user.name}</div>
                <div className="text-xs text-gray-400 truncate">Level {user.level} • {user.faction}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        {Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className="mb-6">
            {!collapsed && (
              <div className="px-4 mb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {getCategoryTitle(category)}
                </h3>
              </div>
            )}
            <nav className="space-y-1">
              {items.map(renderNavItem)}
            </nav>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-white/10">
        {!collapsed && (
          <div className="text-xs text-gray-500 text-center">
            Space Strategy Game
          </div>
        )}
      </div>
    </div>
  )
}

export default Sidebar